import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Spinner,
} from '@chakra-ui/react'
import {
  Play,
  Pause,
  SpeakerHigh,
  SpeakerSlash,
  ArrowsOutSimple,
  ArrowsInSimple,
  DownloadSimple,
  X,
  Gauge,
} from '@phosphor-icons/react'

interface VideoPlayerProps {
  src: string
  isOpen: boolean
  onClose: () => void
  onDownload?: () => void
}

// Formatar duração em mm:ss
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export function VideoPlayer({ src, isOpen, onClose, onDownload }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showControls, setShowControls] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isBuffering, setIsBuffering] = useState(false)
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Velocidades disponíveis
  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

  // Reset state when modal opens ou troca de arquivo
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setIsBuffering(false)
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(0)

      // Timeout de segurança — evita overlay infinito se eventos de mídia não dispararem
      const safetyTimeout = setTimeout(() => {
        setIsLoading(false)
        setIsBuffering(false)
      }, 12000)

      return () => clearTimeout(safetyTimeout)
    }
  }, [isOpen, src])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !isOpen) return

    const handleTimeUpdate = () => setCurrentTime(video.currentTime)
    const handleLoadedMetadata = () => {
      const d = video.duration
      if (isFinite(d) && !isNaN(d)) setDuration(d)
      setIsLoading(false)
    }
    const handleLoadedData = () => {
      setIsLoading(false)
    }
    const handleCanPlay = () => {
      setIsLoading(false)
      setIsBuffering(false)
      if (isOpen) {
        video.play().catch(() => {})
        setIsPlaying(true)
      }
    }
    const handleCanPlayThrough = () => {
      setIsLoading(false)
      setIsBuffering(false)
    }
    const handleWaiting = () => setIsBuffering(true)
    const handlePlaying = () => {
      setIsBuffering(false)
      setIsLoading(false)
      setIsPlaying(true)
    }
    // Dados chegando — evita ficar preso em "Buffering..." após waiting
    const handleProgress = () => setIsBuffering(false)
    const handleEnded = () => setIsPlaying(false)
    const handleError = () => {
      setIsLoading(false)
      setIsBuffering(false)
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('canplaythrough', handleCanPlayThrough)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('playing', handlePlaying)
    video.addEventListener('progress', handleProgress)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)

    // Depois dos listeners: load() pode disparar eventos no mesmo tick
    video.load()

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('canplaythrough', handleCanPlayThrough)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('playing', handlePlaying)
      video.removeEventListener('progress', handleProgress)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
    }
  }, [isOpen, src])

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
    } else {
      video.play().catch(() => {})
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  const toggleMute = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    video.muted = !isMuted
    setIsMuted(!isMuted)
  }, [isMuted])

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return

    if (!isFullscreen) {
      await containerRef.current.requestFullscreen()
    } else {
      await document.exitFullscreen()
    }
  }, [isFullscreen])

  const handleSeek = useCallback((value: number) => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = value
    setCurrentTime(value)
  }, [])

  const handleSpeedChange = useCallback((speed: number) => {
    const video = videoRef.current
    if (!video) return

    video.playbackRate = speed
    setPlaybackRate(speed)
  }, [])

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload()
    } else {
      // Default download behavior
      const link = document.createElement('a')
      link.href = src
      link.download = src.split('/').pop() || 'video.mp4'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [src, onDownload])

  // Auto-hide controls
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }, [isPlaying])

  const handleClose = useCallback(() => {
    const video = videoRef.current
    if (video) {
      video.pause()
    }
    setIsPlaying(false)
    onClose()
  }, [onClose])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        maxW="90vw"
        maxH="90vh"
        m={0}
      >
        <ModalBody p={0}>
          <Box
            ref={containerRef}
            position="relative"
            w="100%"
            bg="black"
            borderRadius="lg"
            overflow="hidden"
            onMouseMove={showControlsTemporarily}
            onMouseLeave={() => isPlaying && setShowControls(false)}
          >
            {/* Close button */}
            <IconButton
              aria-label="Fechar"
              icon={<X size={24} />}
              position="absolute"
              top={2}
              right={2}
              zIndex={10}
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={handleClose}
              opacity={showControls ? 1 : 0}
              transition="opacity 0.3s"
            />

            {/* Video */}
            <video
              ref={videoRef}
              src={src}
              style={{
                width: '100%',
                maxHeight: '80vh',
                cursor: 'pointer',
              }}
              onClick={togglePlay}
            />

            {/* Loading overlay */}
            {(isLoading || isBuffering) && (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="blackAlpha.600"
                zIndex={5}
                direction="column"
                gap={3}
              >
                <Spinner size="xl" color="yellow.400" thickness="4px" />
                <Text color="white" fontSize="sm">
                  {isLoading ? 'Carregando vídeo...' : 'Buffering...'}
                </Text>
              </Flex>
            )}

            {/* Play/Pause overlay */}
            {!isPlaying && !isLoading && (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="blackAlpha.400"
                cursor="pointer"
                onClick={togglePlay}
              >
                <Box
                  bg="yellow.400"
                  borderRadius="full"
                  p={4}
                  _hover={{ bg: 'yellow.300' }}
                  transition="background 0.2s"
                >
                  <Play size={48} weight="fill" color="#1A1A1A" />
                </Box>
              </Flex>
            )}

            {/* Controls */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bg="linear-gradient(transparent, rgba(0,0,0,0.8))"
              px={4}
              py={3}
              opacity={showControls ? 1 : 0}
              transition="opacity 0.3s"
            >
              {/* Progress bar */}
              <Slider
                value={currentTime}
                min={0}
                max={duration || 100}
                onChange={handleSeek}
                mb={2}
                size="sm"
              >
                <SliderTrack bg="whiteAlpha.400">
                  <SliderFilledTrack bg="yellow.400" />
                </SliderTrack>
                <SliderThumb boxSize={3} bg="yellow.400" />
              </Slider>

              {/* Controls row */}
              <HStack spacing={2} justify="space-between">
                <HStack spacing={2}>
                  {/* Play/Pause */}
                  <IconButton
                    aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
                    icon={isPlaying ? <Pause size={20} weight="fill" color="#ECC94B" /> : <Play size={20} weight="fill" color="#ECC94B" />}
                    variant="ghost"
                    size="sm"
                    onClick={togglePlay}
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />

                  {/* Time */}
                  <Text fontSize="sm" color="white">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Text>
                </HStack>

                <HStack spacing={1}>
                  {/* Speed control */}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Velocidade"
                      icon={
                        <HStack spacing={1} color="yellow.400">
                          <Gauge size={18} />
                          <Text fontSize="xs">{playbackRate}x</Text>
                        </HStack>
                      }
                      variant="ghost"
                      size="sm"
                      _hover={{ bg: 'whiteAlpha.200' }}
                    />
                    <Portal>
                      <MenuList bg="gray.800" borderColor="gray.700" minW="100px">
                        {speeds.map((speed) => (
                          <MenuItem
                            key={speed}
                            onClick={() => handleSpeedChange(speed)}
                            bg={playbackRate === speed ? 'yellow.500' : 'transparent'}
                            color={playbackRate === speed ? 'gray.900' : 'white'}
                            _hover={{ bg: playbackRate === speed ? 'yellow.400' : 'gray.700' }}
                          >
                            {speed}x
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Portal>
                  </Menu>

                  {/* Mute */}
                  <IconButton
                    aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
                    icon={isMuted ? <SpeakerSlash size={20} color="#ECC94B" /> : <SpeakerHigh size={20} color="#ECC94B" />}
                    variant="ghost"
                    size="sm"
                    onClick={toggleMute}
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />

                  {/* Download */}
                  <IconButton
                    aria-label="Baixar"
                    icon={<DownloadSimple size={20} color="#ECC94B" />}
                    variant="ghost"
                    size="sm"
                    onClick={handleDownload}
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />

                  {/* Fullscreen */}
                  <IconButton
                    aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    icon={isFullscreen ? <ArrowsInSimple size={20} color="#ECC94B" /> : <ArrowsOutSimple size={20} color="#ECC94B" />}
                    variant="ghost"
                    size="sm"
                    onClick={toggleFullscreen}
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />
                </HStack>
              </HStack>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default VideoPlayer
